import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Alert, Snackbar, Autocomplete, CircularProgress,
  ToggleButtonGroup, ToggleButton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import { TaskAlt, DeleteOutline, WarningAmber } from '@mui/icons-material';
import { procurementService } from '../../services/procurementService';
import { api } from '../../services/apiClient';
import { formatIndianNumber } from '../../utils/currency';
import { useTaxCalculation } from '../../hooks/useTaxCalculation';
import { TaxSummaryPanel, TaxRateCell } from '../../components/common/TaxSummaryPanel';
import {
  P, cardSx, fieldSx, priBtn, outBtn, PX, FieldLabel, StatusPill,
  WorkspaceHeader, TotalsStrip, ValidationStrip,
} from '../../components/common/workspaceKit';

const n = (v: any) => Number(v) || 0;
const r2 = (x: number) => Math.round(x * 100) / 100;
const todayISO = () => new Date().toISOString().slice(0, 10);
const PO_TYPES = ['MATERIAL', 'SERVICE', 'CAPEX', 'CONSUMABLE', 'SUBCONTRACTING', 'JOB_WORK', 'ASSET', 'IMPORT'];

interface LineDraft {
  poItemId: number; productId: number | null; productName: string; description: string;
  hsnCode: string; uom: string; orderedQty: number; receivedQty: number; invoicedQty: number;
  availableToInvoice: number; qty: string; rate: string; gstRate: string; include: boolean;
}

const SupplierInvoiceNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'PO' | 'NONPO'>('PO');
  const [vendors, setVendors] = useState<any[]>([]);

  // shared header
  const [invoiceNo, setInvoiceNo] = useState('');
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [tdsAmount, setTdsAmount] = useState('');

  // PO mode
  const [poType, setPoType] = useState('MATERIAL');
  const [pos, setPos] = useState<any[]>([]);
  const [loadingPos, setLoadingPos] = useState(false);
  const [selectedPo, setSelectedPo] = useState<any | null>(null);
  const [loadingLines, setLoadingLines] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([]);

  // Non-PO mode (header-only amounts)
  const [vendorId, setVendorId] = useState('');
  const [subtotal, setSubtotal] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupMsg, setDupMsg] = useState('');

  useEffect(() => { procurementService.getVendors().then((v: any) => setVendors(v.vendors || v || [])).catch(() => {}); }, []);

  // Load billable POs whenever the PO type changes (PO mode). Not just
  // APPROVED: a PO is normally billed after receipt, by which point it is
  // PARTIALLY_RECEIVED/FULLY_RECEIVED. The API decides the billable set.
  useEffect(() => {
    if (mode !== 'PO') return;
    setLoadingPos(true);
    procurementService.getPurchaseOrdersByType(poType)
      .then((rows) => setPos(rows || []))
      // Surface the failure instead of showing an empty picker: a silently
      // swallowed error here is indistinguishable from "no POs to bill".
      .catch((e: any) => { setPos([]); setError(e?.message || 'Could not load purchase orders'); })
      .finally(() => setLoadingPos(false));
    setSelectedPo(null); setLines([]);
  }, [poType, mode]);

  const onSelectPo = async (po: any | null) => {
    setSelectedPo(po); setLines([]);
    if (!po) return;
    setLoadingLines(true);
    try {
      const items = await procurementService.getPoInvoiceLines(po.id);
      setLines((items || []).map((it: any) => ({
        poItemId: it.poItemId, productId: it.productId ?? null,
        productName: it.productName || it.productCode || `Line ${it.poItemId}`,
        description: it.description || '', hsnCode: it.hsnCode || '', uom: it.uom || '',
        orderedQty: n(it.orderedQty), receivedQty: n(it.receivedQty), invoicedQty: n(it.invoicedQty),
        availableToInvoice: n(it.availableToInvoice),
        qty: String(it.availableToInvoice ?? 0), rate: String(it.unitPrice ?? 0),
        gstRate: String(it.gstRate ?? 0), include: n(it.availableToInvoice) > 0,
      })));
    } catch (e: any) { setError(e.message || 'Failed to load PO lines'); }
    finally { setLoadingLines(false); }
  };

  const setLine = (i: number, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const lineAmount = (l: LineDraft) => r2(n(l.qty) * n(l.rate));
  const lineOver = (l: LineDraft) => l.include && n(l.qty) > l.availableToInvoice + 1e-6;

  const activeLines = lines.filter((l) => l.include && n(l.qty) > 0);
  const anyOver = lines.some(lineOver);

  const poVendorId = selectedPo?.vendor_id ?? selectedPo?.vendorId;

  // Tax is DETERMINED by the server in BOTH modes.
  //
  // The PO mode used to compute a single lumped `qty * rate * gstRate / 100`,
  // which cannot express whether the vendor's supply is intra-state (CGST +
  // SGST) or inter-state (IGST) — and a vendor bill that gets that wrong puts
  // the input credit in the wrong register. The non-PO mode was worse: it
  // asked the user to type the CGST, SGST and IGST amounts by hand.
  const billTaxInput = useMemo(() => {
    if (mode === 'PO') {
      if (!poVendorId || activeLines.length === 0) return null;
      return {
        transactionType: 'PURCHASE_INVOICE' as const,
        partyId: Number(poVendorId),
        documentDate: invoiceDate,
        // A PO-linked bill carries the rate agreed on the PO line; the engine
        // still decides the split and reports the rate as an override.
        allowRateOverride: true,
        lines: activeLines.map((l) => ({
          productId: l.productId ? Number(l.productId) : null,
          hsnCode: l.hsnCode || null,
          quantity: n(l.qty),
          unitPrice: n(l.rate),
          description: l.description || null,
          gstRate: l.gstRate === '' ? null : n(l.gstRate),
        })),
      };
    }
    if (!vendorId || n(subtotal) <= 0) return null;
    return {
      transactionType: 'PURCHASE_INVOICE' as const,
      partyId: Number(vendorId),
      documentDate: invoiceDate,
      lines: [{
        quantity: 1,
        unitPrice: n(subtotal),
        description: remarks || 'Vendor bill',
      }],
    };
  }, [mode, poVendorId, activeLines, vendorId, subtotal, invoiceDate, remarks]);

  const { tax: billTax, loading: billTaxLoading, error: billTaxError } = useTaxCalculation(billTaxInput);

  const totals = useMemo(() => {
    const sub = billTax ? billTax.subtotal
      : (mode === 'PO'
        ? r2(activeLines.reduce((s, l) => s + n(l.qty) * n(l.rate), 0))
        : r2(n(subtotal)));
    const gst = billTax ? billTax.totalTax : 0;
    const total = billTax ? billTax.totalAmount : sub;
    return { sub, gst, total, net: r2(total - n(tdsAmount)) };
  }, [billTax, mode, activeLines, subtotal, tdsAmount]);

  /** The server's figures for one PO-mode grid row, once they arrive. */
  const billTaxLineAt = (i: number) => {
    if (!billTax || mode !== 'PO') return null;
    const activeIndex = activeLines.indexOf(lines[i]);
    return activeIndex >= 0 ? billTax.lines[activeIndex] || null : null;
  };
  const valid = mode === 'PO'
    ? (!!selectedPo && !!invoiceNo && !!invoiceDate && activeLines.length > 0 && !anyOver)
    : (!!vendorId && !!invoiceNo && !!invoiceDate && n(subtotal) > 0);

  const doSubmit = async (allowDuplicate: boolean) => {
    setError(null); setSaving(true);
    try {
      const body: any = mode === 'PO'
        ? {
            vendorId: poVendorId, purchaseOrderId: selectedPo.id, invoiceNo,
            vendorInvoiceNumber: vendorInvoiceNumber || null, invoiceDate, dueDate: dueDate || null,
            remarks, allowDuplicate,
            items: activeLines.map((l) => ({
              poItemId: l.poItemId, productId: l.productId, quantity: n(l.qty), unitPrice: n(l.rate),
              gstRate: n(l.gstRate), hsnCode: l.hsnCode || null, description: l.description || null,
            })),
          }
        : {
            vendorId, purchaseOrderId: null, invoiceNo, vendorInvoiceNumber: vendorInvoiceNumber || null,
            invoiceDate, dueDate: dueDate || null, remarks, allowDuplicate,
            // Sent as a single line rather than as hand-typed CGST/SGST/IGST
            // amounts, so the server determines the split exactly as it does
            // for a PO-linked bill.
            items: [{
              productId: null,
              quantity: 1,
              unitPrice: n(subtotal),
              description: remarks || 'Vendor bill',
            }],
          };
      await api.post('/procurement/purchase-invoices', body);
      setDupOpen(false);
      setToast('Vendor bill created');
      setTimeout(() => navigate('/procurement/invoices'), 800);
    } catch (e: any) {
      if (e?.response?.data?.code === 'DUPLICATE_INVOICE') {
        setDupMsg(e?.response?.data?.error || e?.response?.data?.message || 'A bill with this vendor invoice number already exists for this vendor.');
        setDupOpen(true);
      } else {
        setError(e?.response?.data?.error || e?.message || 'Failed to create bill');
      }
    } finally { setSaving(false); }
  };

  const selectedVendor = vendors.find((v) => String(v.id) === String(vendorId)) || null;
  const poVendorName = selectedPo?.vendor_name || selectedPo?.vendorName
    || vendors.find((v) => String(v.id) === String(poVendorId))?.company_name || '—';

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
      <WorkspaceHeader
        onBack={() => navigate('/procurement/invoices')}
        title="New Vendor Bill"
        subtitle="Record a purchase invoice from a PO (with 3-way match) or a non-PO expense bill."
        chip={<StatusPill label="DRAFT" tone="draft" />}
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate('/procurement/invoices')} disabled={saving} sx={outBtn}>Cancel</Button>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <TaskAlt sx={{ fontSize: 18 }} />}
              onClick={() => doSubmit(false)} disabled={saving || !valid} sx={priBtn}>
              {saving ? 'Saving…' : 'Create Bill'}
            </Button>
          </>
        }
      />

      <Box sx={{ px: PX, pt: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Mode toggle */}
        <ToggleButtonGroup exclusive value={mode} onChange={(_, v) => v && setMode(v)} size="small" sx={{ mb: 2 }}>
          <ToggleButton value="PO" sx={{ textTransform: 'none', fontWeight: 700 }}>From Purchase Order</ToggleButton>
          <ToggleButton value="NONPO" sx={{ textTransform: 'none', fontWeight: 700 }}>Non-PO Expense Bill</ToggleButton>
        </ToggleButtonGroup>

        {/* Source / vendor */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>{mode === 'PO' ? 'Match Source' : 'Supplier'}</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            {mode === 'PO' ? (
              <>
                <Box>
                  <FieldLabel text="PO Type" />
                  <TextField select fullWidth size="small" value={poType} onChange={(e) => setPoType(e.target.value)} sx={fieldSx}>
                    {PO_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
                  </TextField>
                </Box>
                <Box sx={{ gridColumn: { xs: '1', sm: 'span 2', md: 'span 2' } }}>
                  <FieldLabel text="Purchase Order" required />
                  <Autocomplete size="small" options={pos} loading={loadingPos} value={selectedPo}
                    onChange={(_, v) => onSelectPo(v)}
                    getOptionLabel={(o) => `${o.po_number || o.poNumber} · ${formatIndianNumber(n(o.total_amount || o.totalAmount))}`}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    renderInput={(params) => <TextField {...params} placeholder={loadingPos ? 'Loading…' : `Select a ${poType} PO to bill`} sx={fieldSx} />} />
                </Box>
                <Box>
                  <FieldLabel text="Vendor (from PO)" />
                  <TextField fullWidth size="small" value={poVendorName} disabled sx={fieldSx} />
                </Box>
              </>
            ) : (
              <Box sx={{ gridColumn: { xs: '1', sm: 'span 2', md: 'span 2' } }}>
                <FieldLabel text="Supplier" required />
                <Autocomplete size="small" options={vendors} value={selectedVendor}
                  onChange={(_, v) => setVendorId(v ? String(v.id) : '')}
                  getOptionLabel={(o) => o.company_name || o.name || ''}
                  isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                  renderInput={(params) => <TextField {...params} placeholder="Select supplier" sx={fieldSx} />} />
              </Box>
            )}
          </Box>
          {mode === 'PO' && selectedPo && (
            <Box sx={{ px: { xs: 2, md: 2.75 }, pb: 2 }}>
              <Typography sx={{ fontSize: '12px', color: P.muted }}>
                {selectedPo.po_number || selectedPo.poNumber} · {String(selectedPo.po_type || selectedPo.poType || poType).replace(/_/g, ' ')} · PO Total {formatIndianNumber(n(selectedPo.total_amount || selectedPo.totalAmount))}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Header fields */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Invoice Details</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box><FieldLabel text="Invoice No." required /><TextField fullWidth size="small" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} sx={fieldSx} /></Box>
            <Box><FieldLabel text="Vendor Invoice No." /><TextField fullWidth size="small" value={vendorInvoiceNumber} onChange={(e) => setVendorInvoiceNumber(e.target.value)} sx={fieldSx} /></Box>
            <Box><FieldLabel text="Invoice Date" required /><TextField type="date" fullWidth size="small" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} sx={fieldSx} /></Box>
            <Box><FieldLabel text="Due Date" /><TextField type="date" fullWidth size="small" value={dueDate} onChange={(e) => setDueDate(e.target.value)} sx={fieldSx} /></Box>
            <Box sx={{ gridColumn: '1 / -1' }}><FieldLabel text="Remarks" /><TextField fullWidth size="small" value={remarks} onChange={(e) => setRemarks(e.target.value)} sx={fieldSx} /></Box>
          </Box>
        </Paper>

        {/* PO mode: line items */}
        {mode === 'PO' && (
          <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
            <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${P.line}` }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Invoice Lines</Typography>
              {loadingLines && <CircularProgress size={16} />}
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 980, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}>
                <TableHead>
                  <TableRow>
                    {['Product / Description', 'HSN', 'Ordered', 'Received', 'Prev. Inv.', 'Available', 'Qty', 'Rate', 'GST %', 'Amount', ''].map((h, i) => (
                      <TableCell key={h + i} align={['Ordered', 'Received', 'Prev. Inv.', 'Available', 'Qty', 'Rate', 'GST %', 'Amount'].includes(h) ? 'right' : 'left'}
                        sx={{ fontSize: '11px', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '.03em', bgcolor: P.head, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: P.muted, fontSize: '13px' }}>{selectedPo ? 'This PO has nothing left to invoice.' : 'Select a purchase order to load its lines.'}</TableCell></TableRow>
                  ) : lines.map((l, i) => (
                    <TableRow key={l.poItemId} sx={{ opacity: l.include ? 1 : 0.5 }}>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: P.strong }}>{l.productName}</Typography>
                        {l.description && <Typography sx={{ fontSize: '11.5px', color: P.muted }}>{l.description}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: '12.5px', color: P.text }}>{l.hsnCode || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12.5px' }}>{l.orderedQty}{l.uom ? ` ${l.uom}` : ''}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12.5px' }}>{l.receivedQty}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '12.5px' }}>{l.invoicedQty}</TableCell>
                      <TableCell align="right"><Chip size="small" label={l.availableToInvoice} sx={{ height: 20, fontSize: '11.5px', fontWeight: 700, bgcolor: P.greenBg, color: P.green }} /></TableCell>
                      <TableCell align="right" sx={{ width: 96 }}>
                        <TextField size="small" type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} error={lineOver(l)}
                          helperText={lineOver(l) ? 'Over available' : undefined}
                          inputProps={{ style: { textAlign: 'right' } }} sx={{ ...fieldSx, '& .MuiFormHelperText-root': { m: 0, fontSize: '10px', color: P.red } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>
                        <TextField size="small" type="number" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} sx={fieldSx} />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 72 }}>
                        {/* Inherited from the PO line and re-resolved by the
                            engine — the buyer already agreed this rate. */}
                        <TaxRateCell rate={billTaxLineAt(i)?.gstRate ?? n(l.gstRate)} source={billTaxLineAt(i)?.rateSource} overridden={billTaxLineAt(i)?.rateOverridden} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, color: P.strong, whiteSpace: 'nowrap' }}>{formatIndianNumber(lineAmount(l))}</TableCell>
                      <TableCell align="center" sx={{ width: 44 }}>
                        <IconButton size="small" onClick={() => setLine(i, { include: !l.include })} title={l.include ? 'Exclude line' : 'Include line'}>
                          <DeleteOutline sx={{ fontSize: 18, color: l.include ? P.red : P.border }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Non-PO mode: amounts */}
        {mode === 'NONPO' && (
          <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
            <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Amounts</Typography>
            </Box>
            {/* The CGST / SGST / IGST boxes that used to sit here are gone:
                asking a clerk to type the split is exactly how input credit
                lands in the wrong register. Enter the taxable value; the
                engine determines the split from the vendor's state. */}
            <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 380px' }, alignItems: 'start' }}>
              <Box>
                <FieldLabel text="Taxable Value" required />
                <TextField fullWidth size="small" type="number" value={subtotal} onChange={(e) => setSubtotal(e.target.value)} inputProps={{ style: { textAlign: 'right' } }} sx={fieldSx} />
                <Typography sx={{ fontSize: '11.5px', color: P.muted, mt: '6px' }}>
                  The value before tax, as shown on the vendor's bill.
                </Typography>
              </Box>
              <TaxSummaryPanel tax={billTax} loading={billTaxLoading} error={billTaxError} />
            </Box>
          </Paper>
        )}

        {/* TDS */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ p: { xs: 2, md: 2.75 }, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="TDS (deducted at payment)" />
              <TextField fullWidth size="small" type="number" value={tdsAmount} onChange={(e) => setTdsAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} inputProps={{ style: { textAlign: 'right' } }} sx={fieldSx} />
            </Box>
          </Box>
        </Paper>

        {/* Totals + the server-determined tax breakup. In PO mode the panel
            sits beside the strip; in non-PO mode it is already rendered above,
            next to the taxable-value field. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: mode === 'PO' ? '1fr 380px' : '1fr' }, gap: 1.5, mb: 1.5 }}>
          <TotalsStrip cells={[
            { label: 'Subtotal', value: formatIndianNumber(totals.sub) },
            { label: 'GST', value: formatIndianNumber(totals.gst) },
            { label: 'TDS', value: formatIndianNumber(n(tdsAmount)) },
            { label: 'Net Payable', value: formatIndianNumber(totals.net), tone: 'primary' },
          ]} />
          {mode === 'PO' && <TaxSummaryPanel tax={billTax} loading={billTaxLoading} error={billTaxError} />}
        </Box>

        <ValidationStrip items={mode === 'PO' ? [
          { ok: !!selectedPo, label: 'PO selected' },
          { ok: activeLines.length > 0, label: 'At least one line to bill' },
          { ok: !anyOver, label: 'No line over available' },
          { ok: !!invoiceNo && !!invoiceDate, label: 'Invoice no. & date' },
        ] : [
          { ok: !!vendorId, label: 'Vendor selected' },
          { ok: n(subtotal) > 0, label: 'Taxable value entered' },
          { ok: !!invoiceNo && !!invoiceDate, label: 'Invoice no. & date' },
        ]} />
      </Box>

      {/* Duplicate warning */}
      <Dialog open={dupOpen} onClose={() => setDupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}><WarningAmber sx={{ color: P.amber }} /> Possible duplicate bill</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: '13.5px', color: P.text }}>{dupMsg}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDupOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => doSubmit(true)} disabled={saving}>Create Anyway</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default SupplierInvoiceNewPage;
